import { TeamType } from "@redotech/account-common/team-type";
import type { PreviewTeamSettingsChangesResult } from "@redotech/admin-sdk/rpc/schema/settings-approval/preview-settings-changes";
import { ProcessingFeeBasis } from "@redotech/crdb-enums/fulfillment/processing-fee-basis";
import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { ReceivingDisposition } from "@redotech/crdb-enums/fulfillment/receiving-disposition";
import { Currency, CurrencyNames } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import {
  ReFulfillmentBillingConfig,
  ReFulfillmentBillingMode,
} from "@redotech/redo-fulfillment-settings/re-fulfillment";
import CheckIcon from "@redotech/redo-icons/check.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import InfoIcon from "@redotech/redo-icons/info-circle.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import MarkerPin01Icon from "@redotech/redo-icons/marker-pin-01.svg";
import PackageSearchIcon from "@redotech/redo-icons/package-search.svg";
import PackagesIcon from "@redotech/redo-icons/packages.svg";
import Repeat04Icon from "@redotech/redo-icons/repeat-04.svg";
import RouteIcon from "@redotech/redo-icons/route.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import { ReceivingLabelField } from "@redotech/redo-inventory-common/receiving-label";
import {
  GradeAcceptance,
  gradeMappingsFromActions,
  resolveGradeActions,
  resolveWarehouseGradeNames,
  setGradeActionAcceptance,
  setGradeActionRestock,
  type GradeAction,
} from "@redotech/redo-return-settings-common/grade-mapping";
import {
  PROCESSING_CURRENCIES,
  type ProcessingBillingConfig,
} from "@redotech/redo-return-settings-common/processing-billing-config";
import { RECEIVING_DISPOSITION_LABELS } from "@redotech/redo-return-settings-common/receiving-disposition-config";
import { RemitMode } from "@redotech/redo-return-settings-common/remit-mode";
import {
  RETURNS_WAREHOUSE_GRADING_FLOW_SEGMENT,
  RETURNS_WAREHOUSE_VERIFICATION_FLOW_SEGMENT,
} from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { InfoTooltip } from "@redotech/redo-web/arbiter-components/tooltip/info-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { SettingSection } from "@redotech/redo-web/setting-section/setting-section";
import { TabNavigationProvider } from "@redotech/redo-web/setting-section/tab-navigation-provider";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import {
  getTeam,
  updateTeam,
  type ProtectedSettingsAction,
} from "../../client/team";
import { automationUrl, useMerchantAppLogin } from "../merchant-app";
import { LoadTeamContext, TeamContext } from "../team";
import { buildShopifyAdminUrl } from "../teams-utils";
import * as styles from "./managed-returns.module.css";
import { BillingPicker } from "./managed-returns/component/billing-picker";
import { LockedTrack } from "./managed-returns/component/locked-track";
import { MerchantAppLink } from "./managed-returns/component/merchant-app-link";
import { StepCard } from "./managed-returns/component/step-card";
import { TrackHeader } from "./managed-returns/component/track-header";
import { LocationsCard } from "./managed-returns/locations-card";
import { ProcessingBillingExceptions } from "./managed-returns/processing-billing-exceptions";
import { AddReturnsCenterException } from "./managed-returns/returns-center-exception-picker";
import { ThirdPartyFulfillmentBudgetCard } from "./managed-returns/third-party-fulfillment-budget-card";
import {
  useReturnsCenterRows,
  type ReturnsCenterRow,
} from "./managed-returns/use-returns-center-rows";
import {
  reFulfillmentShippingMarkupTeamToFormValue,
  resolveReFulfillmentShippingMarkupForSave,
} from "./re-fulfillment-shipping-markup-form";
import {
  receivingDispositionForSave,
  receivingDispositionFromSettingsGet,
} from "./receiving-disposition-from-settings-get";
import { RequestedChangesPanel } from "./requested-changes-panel";
import { AvailableReturnsCentersTable } from "./returns-package-protection/v2/available-returns-centers-table";
import { CoverageSharePreview } from "./returns-package-protection/v2/coverage-share-preview";
import {
  availableReturnsCentersInput,
  centerReceivingDispositionsForTheirLevels,
  processingCurrencyFeeErrors,
  redoManagedReturnsFormValueToBilling,
  redoManagedReturnsFormValueToReceivingDisposition,
  redoManagedReturnsFormValueToTeam,
  redoManagedReturnsSettingsDefault,
  redoManagedReturnsSettingsForm,
  redoManagedReturnsTeamToFormValue,
  type ExternalLocationHubFormConfig,
  type ManagedReturnsCenterProcessing,
  type ManagedReturnsGradeSettings,
  type RedoManagedReturnsSettingsForm,
} from "./returns-package-protection/v2/redo-managed-returns-settings-form";
import { SettingsApprovalModal } from "./settings-approval-modal";

// ── Form definitions ────────────────────────────────────────────────────────

const reFulfillmentBillingConfigForm = groupInput({
  billingMode: input<"percentage" | "fixed">(),
  revSharePercentage: input<number>({
    validator: numberValidator({ min: 0, max: 100 }),
  }),
  fixedFeeAmount: input<string>({ validator: numberValidator({ min: 0 }) }),
});

const shippingMarkupConfigForm = groupInput({
  markupMode: input<"percentage" | "fixed">(),
  markupPercentage: input<number>({
    validator: numberValidator({ min: 0, max: 100 }),
  }),
  markupAmount: input<string>({ validator: numberValidator({ min: 0 }) }),
});

const reFulfillmentForm = groupInput({
  merchantOwned: reFulfillmentBillingConfigForm,
  shippingMarkup: shippingMarkupConfigForm,
});

type ReFulfillmentValue = InputSchema.Value<typeof reFulfillmentForm>;

const billingConfigDefault: InputSchema.Value<
  typeof reFulfillmentBillingConfigForm
> = { billingMode: "percentage", revSharePercentage: 0, fixedFeeAmount: "0" };

const shippingMarkupDefault: InputSchema.Value<
  typeof shippingMarkupConfigForm
> = reFulfillmentShippingMarkupTeamToFormValue(undefined);

const reFulfillmentDefault: ReFulfillmentValue = {
  merchantOwned: billingConfigDefault,
  shippingMarkup: shippingMarkupDefault,
};

const managedReturnsForm = groupInput(
  {
    redoManagedReturnsSettings: redoManagedReturnsSettingsForm,
    reFulfillment: reFulfillmentForm,
  },
  {
    validator: (value) => {
      if (
        value.redoManagedReturnsSettings.enabled &&
        value.redoManagedReturnsSettings.availableReturnsCenters.length === 0
      ) {
        return [
          "At least one returns center must be configured when Redo Managed Returns is enabled",
        ];
      }
      return [];
    },
  },
);

type ManagedReturnsValue = InputSchema.Value<typeof managedReturnsForm>;

const managedReturnsDefault: ManagedReturnsValue = {
  redoManagedReturnsSettings: redoManagedReturnsSettingsDefault,
  reFulfillment: reFulfillmentDefault,
};

// ── Page root ───────────────────────────────────────────────────────────────

export const ManagedReturnsPage = memo(function ManagedReturnsPage() {
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);
  const client = useRequiredContext(RedoAdminClientContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [automationResult, setAutomationResult] = useState<{
    automationId: string;
    entries: { accountName: string; locationNames: string[] }[];
  } | null>(null);

  const [existingAutomationId, setExistingAutomationId] = useState<
    string | null
  >(null);

  const [billingConfig, setBillingConfig] = useState<
    ProcessingBillingConfig | null | undefined
  >(undefined);
  const [returnProcessingLevel, setReturnProcessingLevel] = useState<
    ProcessingLevel | null | undefined
  >(undefined);
  const [receivingDisposition, setReceivingDisposition] = useState<
    ReceivingDisposition | null | undefined
  >(undefined);
  const [gradeSettings, setGradeSettings] = useState<
    ManagedReturnsGradeSettings | null | undefined
  >(undefined);
  const [centerProcessing, setCenterProcessing] = useState<
    ManagedReturnsCenterProcessing | null | undefined
  >(undefined);

  const refreshBilling = useHandler(async () => {
    try {
      const {
        billing,
        returnProcessingLevel: level,
        receivingDisposition: disposition,
        gradeMappings,
        remitMode,
        returnsCenterProcessingLevels,
        returnsCenterReceivingDispositions,
      } = await rpcClient.getManagedReturnsSettings({ teamId });
      setBillingConfig(billing);
      setReturnProcessingLevel(level);
      setReceivingDisposition(
        receivingDispositionFromSettingsGet(true, disposition),
      );
      setGradeSettings({ gradeMappings, remitMode });
      setCenterProcessing({
        returnsCenterProcessingLevels,
        returnsCenterReceivingDispositions,
      });
    } catch {
      setBillingConfig(null);
      setReturnProcessingLevel(null);
      setReceivingDisposition(receivingDispositionFromSettingsGet(false, null));
      setGradeSettings(null);
      setCenterProcessing(null);
      toast(
        "Couldn't load billing settings; pricing and grade fields are disabled",
        { variant: "error" },
      );
    }
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const {
          billing,
          returnProcessingLevel: level,
          receivingDisposition: disposition,
          gradeMappings,
          remitMode,
          returnsCenterProcessingLevels,
          returnsCenterReceivingDispositions,
        } = await rpcClient.getManagedReturnsSettings({ teamId });
        if (!cancelled) {
          formResetPendingRef.current = true;
          setBillingConfig(billing);
          setReturnProcessingLevel(level);
          setReceivingDisposition(
            receivingDispositionFromSettingsGet(true, disposition),
          );
          setGradeSettings({ gradeMappings, remitMode });
          setCenterProcessing({
            returnsCenterProcessingLevels,
            returnsCenterReceivingDispositions,
          });
        }
      } catch {
        if (!cancelled) {
          setBillingConfig(null);
          setReturnProcessingLevel(null);
          setReceivingDisposition(
            receivingDispositionFromSettingsGet(false, null),
          );
          setGradeSettings(null);
          setCenterProcessing(null);
          toast(
            "Couldn't load billing settings; pricing and grade fields are disabled",
            { variant: "error" },
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rpcClient, teamId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { automationId } = await rpcClient.getRmrAutomations({ teamId });
        if (!cancelled && automationId) {
          setExistingAutomationId(automationId);
        }
      } catch {
        // No automation exists
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rpcClient, teamId]);

  const value: ManagedReturnsValue | undefined = useMemo(() => {
    if (!team) {
      return;
    }

    return {
      redoManagedReturnsSettings: redoManagedReturnsTeamToFormValue(
        team,
        billingConfig ?? null,
        returnProcessingLevel ?? undefined,
        receivingDisposition,
        gradeSettings,
        centerProcessing,
      ),
      reFulfillment: {
        merchantOwned: billingConfigFromTeam(
          team.settings.reFulfillment?.merchantOwned,
        ),
        shippingMarkup: reFulfillmentShippingMarkupTeamToFormValue(
          team.settings.reFulfillment?.shippingMarkup,
        ),
      },
    };
  }, [
    team,
    billingConfig,
    returnProcessingLevel,
    receivingDisposition,
    gradeSettings,
    centerProcessing,
  ]);

  const formInput = useInput(
    managedReturnsForm,
    value || managedReturnsDefault,
  );

  const [approval, setApproval] = useState<{
    preview: PreviewTeamSettingsChangesResult;
    proposedTeam: Awaited<ReturnType<typeof getTeam>>;
    newlyEnabledCenters: { returnsCenterId: string }[];
    billing: ProcessingBillingConfig | null;
    returnProcessingLevel: ProcessingLevel | undefined;
    receivingDisposition: ReceivingDisposition | null | undefined;
    gradeSettings: ManagedReturnsGradeSettings | undefined;
    centerProcessing: ManagedReturnsCenterProcessing | undefined;
  } | null>(null);
  const [committing, setCommitting] = useState(false);
  const [pendingRefreshKey, setPendingRefreshKey] = useState(0);
  const saveModeResolveRef = useRef<(() => void) | null>(null);
  const formResetPendingRef = useRef(false);
  const [approvalReset, setApprovalReset] = useState<{
    staleTeam: typeof team;
  } | null>(null);

  // Approval writes to the team and CRDB settings outside this form; sync the
  // form once both the reloaded team and refreshed settings have landed so the
  // page shows the applied values without a refresh.
  const handleApproved = useHandler(() => {
    const staleTeam = team;
    loadTeam();
    void refreshBilling().then(() => setApprovalReset({ staleTeam }));
  });

  useEffect(() => {
    if (!value) {
      return;
    }
    if (formResetPendingRef.current) {
      formResetPendingRef.current = false;
      formInput.setValue(value);
    } else if (approvalReset && team !== approvalReset.staleTeam) {
      setApprovalReset(null);
      formInput.setValue(value);
    }
  }, [value, formInput, approvalReset, team]);

  const buildProposedTeam = useHandler(
    async (signal: AbortSignal | undefined) => {
      const formValue = formInput.value;
      const totalCoverageSharePercent =
        (Number(
          formValue.redoManagedReturnsSettings.processingCoverageSharePercent,
        ) || 0) +
        (Number(
          formValue.redoManagedReturnsSettings
            .consolidationCoverageSharePercent,
        ) || 0);
      if (totalCoverageSharePercent > 100) {
        return null;
      }

      const currentTeam = await getTeam(client, { teamId, signal });

      const previouslyEnabledIds = new Set(
        redoManagedReturnsTeamToFormValue(currentTeam, null)
          .availableReturnsCenters.filter((c) => c.refulfillment)
          .map((c) => c.returnsCenterId),
      );
      const newlyEnabledCenters =
        formValue.redoManagedReturnsSettings.availableReturnsCenters
          .filter(
            (c) =>
              c.refulfillment && !previouslyEnabledIds.has(c.returnsCenterId),
          )
          .map((c) => ({ returnsCenterId: c.returnsCenterId }));

      currentTeam.settings.redoManagedReturns =
        redoManagedReturnsFormValueToTeam(
          formValue.redoManagedReturnsSettings,
          currentTeam.settings.redoManagedReturns,
        );

      const existingDisposition = currentTeam.settings.returnItemDisposition;
      currentTeam.settings.returnItemDisposition = {
        enabled: formValue.redoManagedReturnsSettings.customGradesEnabled,
        gradeOptions: existingDisposition?.gradeOptions ?? [],
        outcomeOptions: existingDisposition?.outcomeOptions ?? [],
      };

      const shippingMarkup = resolveReFulfillmentShippingMarkupForSave({
        currentShippingMarkup:
          currentTeam.settings.reFulfillment?.shippingMarkup,
        formValue: formValue.reFulfillment.shippingMarkup,
        redoManagedReturnsEnabled: formValue.redoManagedReturnsSettings.enabled,
        returnsCenters:
          formValue.redoManagedReturnsSettings.availableReturnsCenters,
      });

      currentTeam.settings.reFulfillment = {
        ...currentTeam.settings.reFulfillment,
        merchantOwned: billingConfigToTeam(
          formValue.reFulfillment.merchantOwned,
        ),
        ...(shippingMarkup === undefined ? {} : { shippingMarkup }),
      };

      return {
        proposedTeam: currentTeam,
        newlyEnabledCenters,
        billing: billingConfig
          ? redoManagedReturnsFormValueToBilling(
              formValue.redoManagedReturnsSettings,
            )
          : null,
        returnProcessingLevel: returnProcessingLevel
          ? formValue.redoManagedReturnsSettings.returnProcessingLevel
          : undefined,
        receivingDisposition: receivingDispositionForSave(
          receivingDisposition,
          redoManagedReturnsFormValueToReceivingDisposition(
            formValue.redoManagedReturnsSettings,
          ),
        ),
        gradeSettings: gradeSettings
          ? {
              gradeMappings: formValue.redoManagedReturnsSettings.gradeMappings,
              remitMode: formValue.redoManagedReturnsSettings.remitMode,
            }
          : undefined,
        centerProcessing: centerProcessing
          ? {
              returnsCenterProcessingLevels:
                formValue.redoManagedReturnsSettings
                  .returnsCenterProcessingLevels,
              returnsCenterReceivingDispositions:
                centerReceivingDispositionsForTheirLevels(
                  formValue.redoManagedReturnsSettings,
                ),
            }
          : undefined,
      };
    },
  );

  const commit = useHandler(
    async (
      proposed: {
        proposedTeam: Awaited<ReturnType<typeof getTeam>>;
        newlyEnabledCenters: { returnsCenterId: string }[];
        billing: ProcessingBillingConfig | null;
        returnProcessingLevel: ProcessingLevel | undefined;
        receivingDisposition: ReceivingDisposition | null | undefined;
        gradeSettings: ManagedReturnsGradeSettings | undefined;
        centerProcessing: ManagedReturnsCenterProcessing | undefined;
      },
      signal: AbortSignal | undefined,
      protectedSettingsAction?: ProtectedSettingsAction,
    ) => {
      const { proposedTeam, newlyEnabledCenters, billing } = proposed;
      await updateTeam(client, {
        teamId,
        team: proposedTeam,
        signal,
        protectedSettingsAction,
      });

      // putTeam owns remaining team-document fields; this RPC diffs table-backed settings.
      await rpcClient
        .saveManagedReturnsSettings({
          teamId,
          settings: {
            redoManagedReturns: proposedTeam.settings.redoManagedReturns,
            reFulfillment: proposedTeam.settings.reFulfillment,
          },
          billing: billing ?? undefined,
          returnProcessingLevel: proposed.returnProcessingLevel,
          receivingDisposition: proposed.receivingDisposition,
          gradeMappings: proposed.gradeSettings?.gradeMappings,
          remitMode: proposed.gradeSettings?.remitMode,
          returnsCenterProcessingLevels:
            proposed.centerProcessing?.returnsCenterProcessingLevels,
          returnsCenterReceivingDispositions:
            proposed.centerProcessing?.returnsCenterReceivingDispositions,
          protectedSettingsAction,
        })
        .catch((e) => {
          console.warn("Managed returns CRDB settings save failed", e);
          toast("Processing billing settings failed to save", {
            variant: "error",
          });
        });

      await refreshBilling();
      loadTeam();

      if (newlyEnabledCenters.length > 0) {
        const returnsCenterIds = newlyEnabledCenters.map(
          (c) => c.returnsCenterId,
        );
        const { automationId, locationsByAccount } =
          await rpcClient.getRmrAutomations({ teamId, returnsCenterIds });
        if (automationId) {
          setExistingAutomationId(automationId);
          setAutomationResult({
            automationId,
            entries: locationsByAccount ?? [],
          });
        }
      }
    },
  );

  // When the modal opens, the save promise stays pending so PageForm keeps
  // showing the spinner until the user picks a save mode (or discards).
  const save: PageFormSave = useHandler(async (signal) => {
    const built = await buildProposedTeam(signal);
    if (!built) {
      return;
    }
    const preview = await rpcClient.previewTeamSettingsChanges({
      teamId,
      settings: { ...built.proposedTeam.settings },
      billing: built.billing ?? undefined,
      returnProcessingLevel: built.returnProcessingLevel,
      receivingDisposition: built.receivingDisposition,
      gradeMappings: built.gradeSettings?.gradeMappings,
      remitMode: built.gradeSettings?.remitMode,
      returnsCenterProcessingLevels:
        built.centerProcessing?.returnsCenterProcessingLevels,
      returnsCenterReceivingDispositions:
        built.centerProcessing?.returnsCenterReceivingDispositions,
    });
    if (preview.protected.length === 0) {
      await commit(built, signal);
      return;
    }
    setApproval({ ...built, preview });
    await new Promise<void>((resolve) => {
      saveModeResolveRef.current = resolve;
    });
  });

  const handleChooseDisposition = useHandler(
    async (protectedSettingsAction: ProtectedSettingsAction) => {
      if (!approval) {
        return;
      }
      const staleTeam = team;
      setCommitting(true);
      try {
        await commit(approval, undefined, protectedSettingsAction);
        setApproval(null);
      } finally {
        setCommitting(false);
        // Diverted changes leave the server behind the form: once commit's team
        // reload lands, show what was saved. Bypass already matches the form.
        if (protectedSettingsAction !== "bypassApproval") {
          setApprovalReset({ staleTeam });
        }
        setPendingRefreshKey((k) => k + 1);
        saveModeResolveRef.current?.();
        saveModeResolveRef.current = null;
      }
    },
  );

  const handleCloseAutomationResult = useCallback(
    () => setAutomationResult(null),
    [],
  );

  return (
    <>
      {value && billingConfig !== undefined && (
        <ManagedReturnsForm
          billingUnavailable={billingConfig === null}
          existingAutomationId={existingAutomationId}
          id={teamId}
          input={formInput}
          persistentActions={
            <RequestedChangesPanel
              onApproved={handleApproved}
              refreshKey={pendingRefreshKey}
              teamId={teamId}
            />
          }
          save={save}
          value={value}
        />
      )}
      {approval && (
        <SettingsApprovalModal
          onChoose={handleChooseDisposition}
          onClose={() => {
            setApproval(null);
            formInput.setValue(value || managedReturnsDefault);
            saveModeResolveRef.current?.();
            saveModeResolveRef.current = null;
          }}
          pending={committing}
          preview={approval.preview}
        />
      )}
      {automationResult && (
        <AutomationExistsModal
          automationId={automationResult.automationId}
          entries={automationResult.entries}
          onClose={handleCloseAutomationResult}
          teamId={teamId}
        />
      )}
    </>
  );
});

const ManagedReturnsForm = memo(function ManagedReturnsForm({
  id,
  input,
  save,
  value,
  existingAutomationId,
  persistentActions,
  billingUnavailable,
}: {
  id: string;
  input: InputSchema.Input<typeof managedReturnsForm>;
  save: PageFormSave;
  value: ManagedReturnsValue | undefined;
  existingAutomationId: string | null;
  persistentActions?: ReactNode;
  billingUnavailable: boolean;
}) {
  const team = useContext(TeamContext);
  const is3pl = team?.teamType === TeamType.FulfillmentService;

  const [openStep, setOpenStep] = useState<string | null>(null);
  const toggleStep = useCallback(
    (stepId: string) =>
      setOpenStep((prev) => (prev === stepId ? null : stepId)),
    [],
  );

  const { redoManagedReturnsSettings, reFulfillment } = input.inputs;

  const rmrEnabled = redoManagedReturnsSettings.value.enabled;
  const rmrDisabled = !rmrEnabled;
  const coveragePrice = Math.max(
    Number(team?.pricePerOrder) || 0,
    Number(team?.merchantPricePerOrder) || 0,
  );
  const coverageCurrency = getShopCurrencyWithDefault({ team });

  const rmrErrors = input.allErrors;

  return (
    <TabNavigationProvider>
      <div className={styles.pageContainer}>
        <div className={styles.settingsContent}>
          <ErrorBanner errors={rmrErrors} />
          <PageForm
            initial={value || managedReturnsDefault}
            input={input}
            persistentActions={persistentActions}
            save={save}
          >
            <Flex dir="column" gap="3xl">
              {/* ── Returns track ─────────────────────────────────── */}
              <section>
                <TrackHeader
                  enabled={rmrEnabled}
                  onToggle={redoManagedReturnsSettings.inputs.enabled.setValue}
                  title="Redo Managed Returns"
                />
                {rmrEnabled ? (
                  <Flex dir="column" gap="lg">
                    <StepCard
                      description="Centers this merchant partners with."
                      fullWidthBody
                      IconComponent={MarkerPin01Icon}
                      onToggle={() => toggleStep("centers")}
                      open={openStep === "centers"}
                      title="Returns centers"
                    >
                      <ReturnsCentersStep
                        availableReturnsCenters={
                          redoManagedReturnsSettings.inputs
                            .availableReturnsCenters
                        }
                        disabled={rmrDisabled}
                        existingAutomationId={existingAutomationId}
                        hubMappings={
                          redoManagedReturnsSettings.inputs
                            .externalLocationHubMappings.value
                        }
                        onHubMappingChange={(centerId, hub) => {
                          redoManagedReturnsSettings.inputs.externalLocationHubMappings.setValue(
                            {
                              ...redoManagedReturnsSettings.inputs
                                .externalLocationHubMappings.value,
                              [centerId]: hub,
                            },
                          );
                        }}
                        teamId={id}
                      />
                    </StepCard>
                    <StepCard
                      description="How returns are processed at each returns center."
                      IconComponent={PackageSearchIcon}
                      onToggle={() => toggleStep("processing")}
                      open={openStep === "processing"}
                      title="Processing"
                    >
                      <ProcessingStep
                        disabled={rmrDisabled}
                        input={redoManagedReturnsSettings}
                        settingsUnavailable={billingUnavailable}
                        teamId={id}
                      />
                    </StepCard>
                    <StepCard
                      description="Per return, paid by coverage, or both."
                      IconComponent={ShieldDollarIcon}
                      onToggle={() => toggleStep("retbilling")}
                      open={openStep === "retbilling"}
                      title="Returns billing"
                    >
                      <ReturnsBillingStep
                        basis={
                          redoManagedReturnsSettings.inputs.processingFeeBasis
                        }
                        billingUnavailable={billingUnavailable}
                        consolidationCoverageSharePercentValue={
                          redoManagedReturnsSettings.inputs
                            .consolidationCoverageSharePercent.value
                        }
                        coveragePrice={coveragePrice}
                        currency={coverageCurrency}
                        disabled={rmrDisabled}
                        processingCost={
                          redoManagedReturnsSettings.inputs.processingCost
                        }
                        processingCoverageSharePercent={
                          redoManagedReturnsSettings.inputs
                            .processingCoverageSharePercent
                        }
                        processingCurrency={
                          redoManagedReturnsSettings.inputs.processingCurrency
                        }
                        savedProcessingCurrency={
                          redoManagedReturnsSettings.value
                            .savedProcessingCurrency
                        }
                      />
                      <ProcessingBillingExceptions
                        disabled={rmrDisabled || billingUnavailable}
                        input={redoManagedReturnsSettings}
                      />
                    </StepCard>
                  </Flex>
                ) : (
                  <LockedTrack message="Managed Returns is off. Turn it on to configure — all steps must be complete to go live." />
                )}
              </section>

              {/* ── Consolidation track ───────────────────────────── */}
              {rmrEnabled && (
                <section>
                  <Flex dir="column" pb="lg">
                    <Text
                      fontSize="xl"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      Consolidation
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      Batch returns into scheduled consolidated shipments.
                    </Text>
                  </Flex>
                  <Flex dir="column" gap="lg">
                    <StepCard
                      description="Set a consolidation schedule per returns center."
                      fullWidthBody
                      IconComponent={PackagesIcon}
                      onToggle={() => toggleStep("consolidation-centers")}
                      open={openStep === "consolidation-centers"}
                      title="Returns centers"
                    >
                      <AvailableReturnsCentersTable
                        disabled={rmrDisabled}
                        input={
                          redoManagedReturnsSettings.inputs
                            .availableReturnsCenters
                        }
                        readOnly
                        showEditButton
                        teamId={id}
                      />
                    </StepCard>
                    <StepCard
                      description="Paid by coverage, or passed to merchant."
                      IconComponent={ShieldDollarIcon}
                      onToggle={() => toggleStep("consolidation-billing")}
                      open={openStep === "consolidation-billing"}
                      title="Consolidation billing"
                    >
                      <ConsolidationBillingStep
                        consolidationCoverageSharePercent={
                          redoManagedReturnsSettings.inputs
                            .consolidationCoverageSharePercent
                        }
                        coveragePrice={coveragePrice}
                        currency={coverageCurrency}
                        disabled={rmrDisabled}
                        processingCoverageSharePercentValue={
                          redoManagedReturnsSettings.inputs
                            .processingCoverageSharePercent.value
                        }
                      />
                    </StepCard>
                  </Flex>
                </section>
              )}

              {/* ── Refulfillment track ───────────────────────────── */}
              {rmrEnabled && (
                <section>
                  <Flex dir="column" pb="lg">
                    <Text
                      fontSize="xl"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      Refulfillment
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      Ship new orders from restocked stock.
                    </Text>
                  </Flex>
                  <Flex dir="column" gap="lg">
                    <StepCard
                      description="Enable per returns center."
                      fullWidthBody
                      IconComponent={Repeat04Icon}
                      onToggle={() => toggleStep("enable")}
                      open={openStep === "enable"}
                      title="Refulfill from center"
                    >
                      <AvailableReturnsCentersTable
                        disabled={rmrDisabled}
                        input={
                          redoManagedReturnsSettings.inputs
                            .availableReturnsCenters
                        }
                        readOnly
                        showRefulfillmentToggle
                        teamId={id}
                      />
                    </StepCard>
                    <StepCard
                      description="Routing & inventory per provider."
                      IconComponent={RouteIcon}
                      onToggle={() => toggleStep("routing")}
                      open={openStep === "routing"}
                      title="Order routing"
                    >
                      <OrderRoutingStep
                        disabled={rmrDisabled}
                        inventoryPushMinimumQuantity={
                          redoManagedReturnsSettings.inputs
                            .inventoryPushMinimumQuantity
                        }
                        teamId={id}
                      />
                    </StepCard>
                    <StepCard
                      description="Redo vs merchant-owned rates."
                      IconComponent={ShieldDollarIcon}
                      onToggle={() => toggleStep("refbilling")}
                      open={openStep === "refbilling"}
                      title="Refulfillment billing"
                    >
                      <RefulfillmentBillingStep
                        merchantOwned={reFulfillment.inputs.merchantOwned}
                        shippingMarkup={reFulfillment.inputs.shippingMarkup}
                      />
                    </StepCard>
                    <ThirdPartyFulfillmentBudgetCard
                      key={id}
                      merchantTeamId={id}
                    />
                  </Flex>
                </section>
              )}

              {is3pl && (
                <section>
                  <SettingSection
                    collapsible
                    defaultCollapsed
                    description="Manage which locations are designated as returns centers"
                    id="locations"
                    title="Locations"
                  >
                    <LocationsCard teamId={id} />
                  </SettingSection>
                </section>
              )}
            </Flex>
          </PageForm>
        </div>
      </div>
    </TabNavigationProvider>
  );
});

const ReturnsCentersStep = memo(function ReturnsCentersStep({
  availableReturnsCenters,
  hubMappings,
  onHubMappingChange,
  disabled,
  teamId,
  existingAutomationId,
}: {
  availableReturnsCenters: InputSchema.Input<
    typeof availableReturnsCentersInput
  >;
  hubMappings: Record<string, ExternalLocationHubFormConfig>;
  onHubMappingChange: (
    centerId: string,
    hub: ExternalLocationHubFormConfig,
  ) => void;
  disabled: boolean;
  teamId: string;
  existingAutomationId: string | null;
}) {
  return (
    <Flex dir="column" gap="none">
      <AvailableReturnsCentersTable
        disabled={disabled}
        editMode="hub-only"
        headerRight={
          <Flex align="center" gap="lg">
            {existingAutomationId && (
              <Flex
                align="center"
                as="a"
                gap="xxs"
                linkProps={{
                  rel: "noreferrer",
                  target: "_blank",
                  to: automationUrl(teamId, existingAutomationId),
                }}
                textDecoration="none"
              >
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  textColor="brand-primary"
                >
                  Refulfillment automation
                </Text>
                <LinkExternal02Icon
                  className={`${styles.linkIcon} ${styles.linkIconSm}`}
                />
              </Flex>
            )}
            <MerchantAppLink
              label="Configure location flow"
              path="/settings/shipping/location-flow-editor"
              teamId={teamId}
            />
          </Flex>
        }
        hubMappings={hubMappings}
        input={availableReturnsCenters}
        onHubMappingChange={onHubMappingChange}
        teamId={teamId}
      />
    </Flex>
  );
});

// ── Step content: Processing ────────────────────────────────────────────────

function processingLevelLabel(level: ProcessingLevel): string {
  return level.charAt(0).toUpperCase() + level.slice(1);
}

const processingLevelOptions: RedoListItem<ProcessingLevel>[] = Object.values(
  ProcessingLevel,
).map((level) => ({
  type: "text" as const,
  id: level,
  value: level,
  text: processingLevelLabel(level),
}));

const receivingDispositionOptions: RedoListItem<ReceivingDisposition>[] =
  Object.values(ReceivingDisposition).map((disposition) => ({
    type: "text" as const,
    id: disposition,
    value: disposition,
    text: RECEIVING_DISPOSITION_LABELS[disposition],
  }));

const receivingLabelFieldLabels: Record<ReceivingLabelField, string> = {
  [ReceivingLabelField.returnId]: "Return ID",
  [ReceivingLabelField.orderName]: "Order name",
};

const receivingLabelFieldOptions: RedoListItem<ReceivingLabelField>[] =
  Object.values(ReceivingLabelField).map((field) => ({
    type: "text" as const,
    id: field,
    value: field,
    text: receivingLabelFieldLabels[field],
  }));

const remitModeLabels: Record<RemitMode, string> = {
  [RemitMode.AUTOMATIC]: "Automatic",
  [RemitMode.MANUAL]: "Manual",
};

const remitModeOptions: RedoListItem<RemitMode>[] = Object.values(
  RemitMode,
).map((mode) => ({
  type: "text" as const,
  id: mode,
  value: mode,
  text: remitModeLabels[mode],
}));

const ACCEPTANCE_OPTIONS: RedoListItem<GradeAcceptance>[] = [
  {
    id: GradeAcceptance.ACCEPT,
    type: "text",
    text: "Accept",
    value: GradeAcceptance.ACCEPT,
    leadingItem: { type: "status", color: "green" },
  },
  {
    id: GradeAcceptance.FLAG_FOR_REVIEW,
    type: "text",
    text: "Flag for review",
    value: GradeAcceptance.FLAG_FOR_REVIEW,
    leadingItem: { type: "status", color: "yellow" },
  },
  {
    id: GradeAcceptance.REJECT,
    type: "text",
    text: "Reject",
    value: GradeAcceptance.REJECT,
    leadingItem: { type: "status", color: "red" },
  },
];

interface ReturnsCenterLevelRow extends ReturnsCenterRow {
  level: ProcessingLevel;
  receivingDisposition: ReceivingDisposition | null;
  /** The center sets its own level or receiving disposition instead of following the default. */
  overridesDefault: boolean;
}

/** Levels with settings shared by every center on them; receiving centers each set their own disposition. */
const serviceLevels = [ProcessingLevel.Grading, ProcessingLevel.Verification];

const CENTER_LEVEL_COLUMN_WIDTH = "240px";

const ProcessingStep = memo(function ProcessingStep({
  input,
  disabled,
  settingsUnavailable,
  teamId,
}: {
  input: RedoManagedReturnsSettingsForm;
  disabled: boolean;
  /** Processing settings failed to load from CRDB, so edits to them could not be saved. */
  settingsUnavailable: boolean;
  teamId: string;
}) {
  const team = useContext(TeamContext);
  const openMerchantApp = useMerchantAppLogin();

  const returnsCenters = useReturnsCenterRows(
    input.inputs.availableReturnsCenters.value ?? [],
  );

  const globalProcessingLevel = input.inputs.returnProcessingLevel.value;
  const globalReceivingDisposition = input.inputs.receivingDisposition.value;
  const centerProcessingLevels = input.inputs.returnsCenterProcessingLevels;
  const centerReceivingDispositions =
    input.inputs.returnsCenterReceivingDispositions;

  const handleDefaultLevelSelected = useCallback(
    ({ item }: { item: RedoListItem<ProcessingLevel> }) => {
      input.inputs.returnProcessingLevel.setValue(item.value);
      centerProcessingLevels.setValue(
        withoutDefaultValue(centerProcessingLevels.value, item.value),
      );
    },
    [input.inputs.returnProcessingLevel, centerProcessingLevels],
  );

  const setCenterLevel = useCallback(
    (returnsCenterId: string, level: ProcessingLevel) => {
      centerProcessingLevels.setValue({
        ...centerProcessingLevels.value,
        [returnsCenterId]: level === globalProcessingLevel ? null : level,
      });
      // A receiving disposition only applies while the center receives.
      if (level !== ProcessingLevel.Receiving) {
        centerReceivingDispositions.setValue({
          ...centerReceivingDispositions.value,
          [returnsCenterId]: null,
        });
      }
    },
    [
      centerProcessingLevels,
      centerReceivingDispositions,
      globalProcessingLevel,
    ],
  );

  const setDefaultReceivingDisposition = useCallback(
    (disposition: ReceivingDisposition) => {
      input.inputs.receivingDisposition.setValue(disposition);
      centerReceivingDispositions.setValue(
        withoutDefaultValue(centerReceivingDispositions.value, disposition),
      );
    },
    [input.inputs.receivingDisposition, centerReceivingDispositions],
  );

  const setCenterReceivingDisposition = useCallback(
    (returnsCenterId: string, disposition: ReceivingDisposition | null) =>
      centerReceivingDispositions.setValue({
        ...centerReceivingDispositions.value,
        [returnsCenterId]:
          disposition === globalReceivingDisposition ? null : disposition,
      }),
    [centerReceivingDispositions, globalReceivingDisposition],
  );

  const selectedRemitMode = useMemo(
    () =>
      remitModeOptions.find((o) => o.value === input.inputs.remitMode.value),
    [input.inputs.remitMode.value],
  );

  const handleRemitModeSelected = useCallback(
    ({ item }: { item: RedoListItem<RemitMode> }) => {
      input.inputs.remitMode.setValue(item.value);
    },
    [input.inputs.remitMode],
  );

  const openProcessingFlow = useCallback(
    (level: ProcessingLevel) =>
      openMerchantApp(
        teamId,
        `/settings/warehouse/processing-flow/${
          level === ProcessingLevel.Grading
            ? RETURNS_WAREHOUSE_GRADING_FLOW_SEGMENT
            : RETURNS_WAREHOUSE_VERIFICATION_FLOW_SEGMENT
        }`,
      ),
    [openMerchantApp, teamId],
  );

  const customGradesEnabled = input.inputs.customGradesEnabled.value;

  const merchantGradeNames = useMemo(
    () =>
      resolveWarehouseGradeNames({
        customGradesEnabled,
        gradeOptions: team?.settings.returnItemDisposition?.gradeOptions,
      }),
    [customGradesEnabled, team],
  );

  const gradeActions = useMemo(
    () =>
      resolveGradeActions(merchantGradeNames, input.inputs.gradeMappings.value),
    [merchantGradeNames, input.inputs.gradeMappings.value],
  );

  const setGradeAcceptance = useCallback(
    (gradeName: string, acceptance: GradeAcceptance) => {
      input.inputs.gradeMappings.setValue(
        gradeMappingsFromActions(
          setGradeActionAcceptance(gradeActions, gradeName, acceptance),
        ),
      );
    },
    [gradeActions, input.inputs.gradeMappings],
  );

  const setGradeRestock = useCallback(
    (gradeName: string, restock: boolean) => {
      input.inputs.gradeMappings.setValue(
        gradeMappingsFromActions(
          setGradeActionRestock(gradeActions, gradeName, restock),
        ),
      );
    },
    [gradeActions, input.inputs.gradeMappings],
  );

  const centerRows = useMemo(
    (): ReturnsCenterLevelRow[] =>
      returnsCenters.map((center) => {
        const ownLevel = centerProcessingLevels.value[center.id];
        const ownDisposition = centerReceivingDispositions.value[center.id];
        const level = ownLevel ?? globalProcessingLevel;
        return {
          ...center,
          level,
          receivingDisposition: ownDisposition ?? globalReceivingDisposition,
          // A leftover disposition doesn't count once the center no longer receives.
          overridesDefault:
            ownLevel != null ||
            (level === ProcessingLevel.Receiving && ownDisposition != null),
        };
      }),
    [
      returnsCenters,
      centerProcessingLevels.value,
      centerReceivingDispositions.value,
      globalProcessingLevel,
      globalReceivingDisposition,
    ],
  );

  const serviceTabs = serviceLevels.filter(
    (level) =>
      globalProcessingLevel === level ||
      centerRows.some((center) => center.level === level),
  );
  const [selectedService, setSelectedService] =
    useState<ProcessingLevel | null>(null);
  const service =
    selectedService && serviceTabs.includes(selectedService)
      ? selectedService
      : serviceTabs[0];
  const serviceTabOptions = serviceTabs.map((level) => {
    const count = centerRows.filter((center) => center.level === level).length;
    return {
      value: level,
      el: (
        <Flex align="baseline" gap="xs">
          <Text fontSize="sm" fontWeight="semibold">
            {processingLevelLabel(level)}
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            {count} {count === 1 ? "center" : "centers"}
          </Text>
        </Flex>
      ),
    };
  });

  const processingFlowLink = (
    <Flex
      align="center"
      borderColor="primary"
      borderRadius="lg"
      borderWidth="1px"
      className={styles.linkRow}
      gap="lg"
      onClick={() => service && openProcessingFlow(service)}
      p="lg"
    >
      <Flex dir="column" gap="xxs" grow="1">
        <Text fontSize="xs" fontWeight="semibold">
          Warehouse processing flow
        </Text>
        <Text fontSize="xxs" textColor="secondary">
          Edit the flow warehouse teams complete when processing returns.
        </Text>
      </Flex>
      <ChevronRightIcon className={styles.linkIcon} />
    </Flex>
  );

  return (
    <Flex dir="column" gap="xl" pt="lg">
      <RedoSingleSelectDropdownInput
        description="When an accepted return is remitted. Manual leaves accepted returns awaiting a merchant remit decision."
        disabled={disabled || settingsUnavailable}
        label="Remit mode"
        options={remitModeOptions}
        optionSelected={handleRemitModeSelected}
        placeholder="Select remit mode..."
        selectedItem={selectedRemitMode}
        size="xs"
      />
      <RedoSingleSelectDropdownInput
        description="The default for every returns center. Add an exception for a center that processes differently."
        disabled={disabled || settingsUnavailable}
        label="Processing level"
        options={processingLevelOptions}
        optionSelected={handleDefaultLevelSelected}
        selectedItem={processingLevelOptions.find(
          (o) => o.value === globalProcessingLevel,
        )}
        size="xs"
      />
      {globalProcessingLevel === ProcessingLevel.Receiving && (
        <RedoSingleSelectDropdownInput
          description="Where received items are sorted when processing level is receiving."
          disabled={disabled || settingsUnavailable}
          label="Receiving disposition"
          options={receivingDispositionOptions}
          optionSelected={({ item }) =>
            setDefaultReceivingDisposition(item.value)
          }
          placeholder="Select receiving disposition..."
          selectedItem={receivingDispositionOptions.find(
            (o) => o.value === globalReceivingDisposition,
          )}
          size="xs"
          state={globalReceivingDisposition == null ? "error" : "default"}
        />
      )}
      {centerRows.length > 0 && (
        <ReturnsCenterLevelExceptions
          centers={centerRows}
          disabled={disabled || settingsUnavailable}
          setLevel={setCenterLevel}
          setReceivingDisposition={setCenterReceivingDisposition}
        />
      )}
      {service && (
        <>
          <Divider />
          <Flex dir="column" gap="xl">
            <Text fontSize="sm" fontWeight="semibold">
              Service Settings
            </Text>
            {serviceTabOptions.length > 1 && (
              <RedoButtonTabGroup
                onChange={setSelectedService}
                options={serviceTabOptions}
                value={service}
              />
            )}
            {service === ProcessingLevel.Grading && (
              <>
                <RedoToggle
                  description="Use the merchant's custom grades instead of the default A–E warehouse grades."
                  disabled={disabled}
                  label="Use custom grades"
                  setValue={input.inputs.customGradesEnabled.setValue}
                  size="sm"
                  value={input.inputs.customGradesEnabled.value}
                />
                {customGradesEnabled ? (
                  <GradeActionsTable
                    disabled={disabled || settingsUnavailable}
                    gradeActions={gradeActions}
                    setAcceptance={setGradeAcceptance}
                    setRestock={setGradeRestock}
                    teamId={teamId}
                  />
                ) : (
                  <Flex dir="column" gap="sm">
                    <Flex dir="column">
                      <Text fontSize="sm" fontWeight="semibold">
                        Grade actions
                      </Text>
                      <Text fontSize="xs" textColor="secondary">
                        What the warehouse does when a grade is assigned during
                        processing
                      </Text>
                    </Flex>
                    <RedoAlert
                      color="default"
                      description="Grades A, B and C are accepted and restocked; D and E are rejected. Enable custom grades to configure per-grade actions."
                      fullWidth
                      Icon={InfoIcon}
                      title="Using Redo's default grades"
                    />
                  </Flex>
                )}
              </>
            )}
            {service === ProcessingLevel.Verification && (
              <VerificationSettings disabled={disabled} input={input} />
            )}
            {processingFlowLink}
          </Flex>
        </>
      )}
    </Flex>
  );
});

/** Returns centers that don't follow the default processing level: add one, then set its level. */
const ReturnsCenterLevelExceptions = memo(
  function ReturnsCenterLevelExceptions({
    centers,
    disabled,
    setLevel,
    setReceivingDisposition,
  }: {
    centers: readonly ReturnsCenterLevelRow[];
    disabled: boolean;
    setLevel: (returnsCenterId: string, level: ProcessingLevel) => void;
    setReceivingDisposition: (
      returnsCenterId: string,
      disposition: ReceivingDisposition,
    ) => void;
  }) {
    // A center added here stays listed until it's changed; set back to the default, it drops off.
    const [addedCenterIds, setAddedCenterIds] = useState<readonly string[]>([]);
    const settle = (returnsCenterId: string) =>
      setAddedCenterIds((ids) => ids.filter((id) => id !== returnsCenterId));
    const exceptions = centers.filter(
      (center) => center.overridesDefault || addedCenterIds.includes(center.id),
    );
    const addable = centers.filter((center) => !exceptions.includes(center));

    return (
      <RedoCollapseSubsection
        defaultCollapsed={false}
        title={`Returns center exceptions (${exceptions.length})`}
      >
        <Flex dir="column" gap="md">
          {exceptions.length > 0 && (
            <ReturnsCenterLevelsTable
              centers={exceptions}
              disabled={disabled}
              setLevel={(returnsCenterId, level) => {
                settle(returnsCenterId);
                setLevel(returnsCenterId, level);
              }}
              setReceivingDisposition={(returnsCenterId, disposition) => {
                settle(returnsCenterId);
                setReceivingDisposition(returnsCenterId, disposition);
              }}
            />
          )}
          {addable.length > 0 && (
            <AddReturnsCenterException
              centers={addable}
              disabled={disabled}
              onAdd={(returnsCenterId) =>
                setAddedCenterIds((ids) => [...ids, returnsCenterId])
              }
            />
          )}
        </Flex>
      </RedoCollapseSubsection>
    );
  },
);

/** Each listed center's processing level, with its receiving disposition when it receives. */
const ReturnsCenterLevelsTable = memo(function ReturnsCenterLevelsTable({
  centers,
  disabled,
  setLevel,
  setReceivingDisposition,
}: {
  centers: readonly ReturnsCenterLevelRow[];
  disabled: boolean;
  setLevel: (returnsCenterId: string, level: ProcessingLevel) => void;
  setReceivingDisposition: (
    returnsCenterId: string,
    disposition: ReceivingDisposition,
  ) => void;
}) {
  return (
    <Flex
      borderColor="primary"
      borderRadius="lg"
      borderWidth="1px"
      dir="column"
      overflow="hidden"
    >
      <Flex
        align="center"
        bgColor="secondary"
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        gap="md"
        px="xl"
        py="xs"
      >
        <Flex grow={1}>
          <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
            RETURNS CENTER
          </Text>
        </Flex>
        <Flex basis={CENTER_LEVEL_COLUMN_WIDTH} shrink={0}>
          <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
            PROCESSING LEVEL
          </Text>
        </Flex>
      </Flex>
      {centers.map((center, index) => (
        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth={index > 0 ? "1px" : "none"}
          gap="md"
          key={center.id}
          px="xl"
          py="lg"
        >
          <Flex dir="column" gap="xxs" grow={1} minW="none">
            <Text fontSize="xs" fontWeight="medium">
              {center.name}
            </Text>
            {center.location && (
              <Text fontSize="xxs" textColor="secondary">
                {center.location}
              </Text>
            )}
          </Flex>
          <Flex
            basis={CENTER_LEVEL_COLUMN_WIDTH}
            dir="column"
            gap="sm"
            shrink={0}
          >
            <RedoSingleSelectDropdownInput
              disabled={disabled}
              fullWidth
              options={processingLevelOptions}
              optionSelected={({ item }) => setLevel(center.id, item.value)}
              selectedItem={processingLevelOptions.find(
                (o) => o.value === center.level,
              )}
              size="xs"
            />
            {center.level === ProcessingLevel.Receiving && (
              <RedoSingleSelectDropdownInput
                disabled={disabled}
                fullWidth
                label="Disposition"
                options={receivingDispositionOptions}
                optionSelected={({ item }) =>
                  setReceivingDisposition(center.id, item.value)
                }
                placeholder="Select receiving disposition..."
                selectedItem={receivingDispositionOptions.find(
                  (o) => o.value === center.receivingDisposition,
                )}
                size="xs"
                state={
                  center.receivingDisposition == null ? "error" : "default"
                }
              />
            )}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});

/** Settings every center on verification shares. */
const VerificationSettings = memo(function VerificationSettings({
  input,
  disabled,
}: {
  input: RedoManagedReturnsSettingsForm;
  disabled: boolean;
}) {
  const selectedReceivingLabelField = useMemo(
    () =>
      receivingLabelFieldOptions.find(
        (o) => o.value === input.inputs.receivingLabelEncodedField.value,
      ),
    [input.inputs.receivingLabelEncodedField.value],
  );

  const handleReceivingLabelFieldSelected = useCallback(
    ({ item }: { item: RedoListItem<ReceivingLabelField> }) => {
      input.inputs.receivingLabelEncodedField.setValue(item.value);
    },
    [input.inputs.receivingLabelEncodedField],
  );

  const verificationOutcomeRows = useMemo(
    () =>
      [
        {
          key: "pass",
          label: "Pass",
          input: input.inputs.verificationPassOutcome,
        },
        {
          key: "fail",
          label: "Fail",
          input: input.inputs.verificationFailOutcome,
        },
      ] as const,
    [
      input.inputs.verificationPassOutcome,
      input.inputs.verificationFailOutcome,
    ],
  );

  return (
    <>
      <Flex dir="column" gap="sm">
        <Flex dir="column">
          <Text fontSize="sm" fontWeight="semibold">
            Verification outcomes
          </Text>
          <Text fontSize="xs" textColor="secondary">
            What the warehouse does when a return passes or fails verification.
            Accept remits per the remit mode above.
          </Text>
        </Flex>
        <Flex
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          dir="column"
          overflow="hidden"
        >
          <Flex
            align="center"
            bgColor="secondary"
            borderBottomWidth="1px"
            gap="md"
            px="xl"
            py="xs"
          >
            <Flex grow={1}>
              <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
                VERIFICATION
              </Text>
            </Flex>
            <Flex basis="180px" minW="none" shrink={0}>
              <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
                ACCEPTANCE
              </Text>
            </Flex>
          </Flex>
          {verificationOutcomeRows.map((row, index) => (
            <Flex
              align="center"
              borderBottomWidth={
                index === verificationOutcomeRows.length - 1 ? "none" : "1px"
              }
              gap="md"
              key={row.key}
              px="xl"
              py="lg"
            >
              <Flex align="center" grow={1}>
                <Text fontSize="xs" fontWeight="semibold">
                  {row.label}
                </Text>
              </Flex>
              <Flex align="center" basis="180px" minW="none" shrink={0}>
                <RedoSingleSelectDropdownInput<GradeAcceptance>
                  disabled={disabled}
                  fullWidth
                  options={ACCEPTANCE_OPTIONS}
                  optionSelected={({ item }) => row.input.setValue(item.value)}
                  selectedItem={ACCEPTANCE_OPTIONS.find(
                    (i) => i.value === row.input.value,
                  )}
                  size="sm"
                />
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <RedoToggle
        description="Print an additional QR receiving label, alongside the license plate, that this merchant's 3PL scans to receive the return."
        disabled={disabled}
        label="Receiving label"
        setValue={input.inputs.receivingLabelEnabled.setValue}
        size="sm"
        value={input.inputs.receivingLabelEnabled.value}
      />
      {input.inputs.receivingLabelEnabled.value && (
        <RedoSingleSelectDropdownInput
          description="Data encoded in the receiving label's QR code and printed value."
          disabled={disabled}
          label="Encoded value"
          options={receivingLabelFieldOptions}
          optionSelected={handleReceivingLabelFieldSelected}
          placeholder="Select encoded value..."
          selectedItem={selectedReceivingLabelField}
          size="xs"
        />
      )}
    </>
  );
});

/** Centers whose override now equals the default follow it instead. */
function withoutDefaultValue<T>(
  centerValues: Readonly<Record<string, T | null>>,
  defaultValue: T,
): Record<string, T | null> {
  return Object.fromEntries(
    Object.entries(centerValues).map(([centerId, value]) => [
      centerId,
      value === defaultValue ? null : value,
    ]),
  );
}

// ── Grade actions table ────────────────────────────────────────────────────

const GradeActionsTable = memo(function GradeActionsTable({
  gradeActions,
  setAcceptance,
  setRestock,
  disabled,
  teamId,
}: {
  gradeActions: GradeAction[];
  setAcceptance: (gradeName: string, acceptance: GradeAcceptance) => void;
  setRestock: (gradeName: string, restock: boolean) => void;
  disabled: boolean;
  teamId: string;
}) {
  return (
    <Flex dir="column" gap="sm">
      <Flex align="center" justify="space-between">
        <Flex dir="column">
          <Text fontSize="sm" fontWeight="semibold">
            Grade actions
          </Text>
          <Text fontSize="xs" textColor="secondary">
            What the warehouse does when a grade is assigned during processing
          </Text>
        </Flex>
        <MerchantAppLink
          label="Grade settings"
          path="/settings/returns-ui"
          teamId={teamId}
        />
      </Flex>
      {gradeActions.length === 0 ? (
        <Text fontSize="xs" textColor="secondary">
          No grades configured. Add grades in the merchant's Returns UI
          settings.
        </Text>
      ) : (
        <Flex
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          dir="column"
          overflow="hidden"
        >
          <Flex
            align="center"
            bgColor="secondary"
            borderBottomWidth="1px"
            gap="md"
            px="md"
            py="xs"
          >
            <Flex grow={1}>
              <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
                GRADE
              </Text>
            </Flex>
            <Flex basis="192px" shrink={0}>
              <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
                ACCEPTANCE
              </Text>
            </Flex>
            <Flex basis="64px" ml="xl" shrink={0}>
              <Text fontSize="xxs" fontWeight="semibold" textColor="secondary">
                RESTOCK
              </Text>
            </Flex>
          </Flex>
          {gradeActions.map(({ gradeName, mapping }, index) => {
            const disableRestock =
              !mapping || mapping.acceptance === GradeAcceptance.REJECT;
            return (
              <Flex
                align="center"
                borderBottomWidth={
                  index === gradeActions.length - 1 ? "none" : "1px"
                }
                gap="md"
                key={gradeName}
                px="xl"
                py="lg"
              >
                <Flex align="center" grow={1}>
                  <Flex
                    align="center"
                    bgColor="secondary-solid"
                    borderRadius="md"
                    justify="center"
                    px="lg"
                    py="sm"
                    shrink={0}
                  >
                    <Text fontSize="xs" fontWeight="semibold" textColor="white">
                      {gradeName}
                    </Text>
                  </Flex>
                </Flex>
                <Flex align="center" basis="192px" shrink={0}>
                  <RedoSingleSelectDropdownInput<GradeAcceptance>
                    description={mapping ? undefined : "Required"}
                    disabled={disabled}
                    fullWidth
                    options={ACCEPTANCE_OPTIONS}
                    optionSelected={({ item }) =>
                      setAcceptance(gradeName, item.value)
                    }
                    placeholder="Select acceptance"
                    selectedItem={ACCEPTANCE_OPTIONS.find(
                      (i) => i.value === mapping?.acceptance,
                    )}
                    size="sm"
                    state={mapping ? "default" : "error"}
                  />
                </Flex>
                <Flex align="center" basis="64px" ml="xl" shrink={0}>
                  <RedoToggle
                    disabled={disabled || disableRestock}
                    setValue={(value) => setRestock(gradeName, value)}
                    size="sm"
                    value={!disableRestock && (mapping?.restock ?? false)}
                  />
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
});

// ── Step content: Returns billing ───────────────────────────────────────────

const feeBasisOptions: { value: ProcessingFeeBasis; el: ReactNode }[] = [
  { value: ProcessingFeeBasis.PerReturn, el: "Per return" },
  { value: ProcessingFeeBasis.PerItem, el: "Per unit" },
];

const processingCurrencyOptions: RedoListItem<Currency>[] =
  PROCESSING_CURRENCIES.map((value) => ({
    type: "text",
    id: value,
    text: `${value} — ${CurrencyNames[value]}`,
    value,
  }));

const ReturnsBillingStep = memo(function ReturnsBillingStep({
  basis,
  processingCost,
  processingCurrency,
  savedProcessingCurrency,
  processingCoverageSharePercent,
  consolidationCoverageSharePercentValue,
  coveragePrice,
  currency,
  disabled,
  billingUnavailable,
}: {
  basis: InputSchema.Input<ReturnType<typeof input<ProcessingFeeBasis>>>;
  processingCurrency: InputSchema.Input<ReturnType<typeof input<Currency>>>;
  processingCost: InputSchema.Input<ReturnType<typeof input<string>>>;
  savedProcessingCurrency: Currency;
  processingCoverageSharePercent: InputSchema.Input<
    ReturnType<typeof input<string>>
  >;
  consolidationCoverageSharePercentValue: string;
  coveragePrice: number;
  currency: Currency;
  disabled: boolean;
  billingUnavailable: boolean;
}) {
  const billingDisabled = disabled || billingUnavailable;
  const totalCoverageSharePercent =
    (Number(processingCoverageSharePercent.value) || 0) +
    (Number(consolidationCoverageSharePercentValue) || 0);
  const exceedsCoverageShareLimit = totalCoverageSharePercent > 100;
  const currencyFeeErrors = processingCurrencyFeeErrors({
    processingCost: processingCost.value,
    processingCurrency: processingCurrency.value,
    savedProcessingCurrency,
  });

  return (
    <Flex dir="column" gap="xl" pt="lg">
      {exceedsCoverageShareLimit && (
        <Text fontSize="xs" textColor="error">
          {`Total coverage percentage (${totalCoverageSharePercent.toFixed(2)}%) cannot exceed 100%`}
        </Text>
      )}
      {billingUnavailable && (
        <Text fontSize="xs" textColor="error">
          Billing settings failed to load; reload the page to edit the fee and
          fee basis.
        </Text>
      )}
      <Flex align="flex-start" gap="md">
        <Flex basis={0} dir="column" gap="xs" grow="1">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Fee basis
          </Text>
          <RedoButtonTabGroup
            disabled={billingDisabled}
            fullWidth={false}
            onChange={basis.setValue}
            options={feeBasisOptions}
            size="sm"
            value={basis.value}
          />
        </Flex>
        <Flex basis={0} dir="column" gap="xs" grow="1">
          <Flex align="center" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Processing currency
            </Text>
            <InfoTooltip
              boxSize={14}
              tooltip={{
                title:
                  "Default for centers without a currency exception. Changing currency does not convert amounts. Your invoice may use a different currency.",
                leftAlign: true,
              }}
            />
          </Flex>
          <RedoSingleSelectDropdownInput<Currency>
            ariaLabel="Processing currency"
            disabled={billingDisabled}
            options={processingCurrencyOptions}
            optionSelected={({ item }) =>
              processingCurrency.setValue(item.value)
            }
            selectedItem={processingCurrencyOptions.find(
              (item) => item.value === processingCurrency.value,
            )}
            size="xs"
          />
        </Flex>
      </Flex>
      <Flex align="flex-start" gap="md">
        <Flex basis={0} dir="column" gap="xs" grow="1">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {basis.value === ProcessingFeeBasis.PerItem
              ? "Per-unit processing fee"
              : "Per-return processing fee"}
          </Text>
          <RedoTextInput
            IconTrailing={() => (
              <Text as="span">{processingCurrency.value}</Text>
            )}
            inputTypeHint="number"
            min={0}
            placeholder="0.00"
            setValue={processingCost.setValue}
            state={billingDisabled ? "disabled" : "default"}
            step={0.01}
            value={processingCost.value}
          />
          {currencyFeeErrors.map((error) => (
            <Text fontSize="xs" key={error} textColor="error">
              {error}
            </Text>
          ))}
        </Flex>
        <Flex basis={0} dir="column" gap="xs" grow="1">
          <Flex align="center" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Paid for by coverage
            </Text>
            <Link to="../returns/return-protection">
              <LinkExternal02Icon
                className={`${styles.linkIcon} ${styles.linkIconSm}`}
              />
            </Link>
          </Flex>
          <RedoTextInput
            IconTrailing={() => <Text as="span">%</Text>}
            inputTypeHint="number"
            max={100}
            min={0}
            placeholder="0"
            setValue={processingCoverageSharePercent.setValue}
            state={
              disabled
                ? "disabled"
                : exceedsCoverageShareLimit
                  ? "error"
                  : "default"
            }
            step={0.01}
            value={processingCoverageSharePercent.value}
          />
          <CoverageSharePreview
            coveragePrice={coveragePrice}
            currency={currency}
            percentValue={processingCoverageSharePercent.value}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

// ── Step content: Consolidation billing ─────────────────────────────────────

const ConsolidationBillingStep = memo(function ConsolidationBillingStep({
  consolidationCoverageSharePercent,
  processingCoverageSharePercentValue,
  coveragePrice,
  currency,
  disabled,
}: {
  consolidationCoverageSharePercent: InputSchema.Input<
    ReturnType<typeof input<string>>
  >;
  processingCoverageSharePercentValue: string;
  coveragePrice: number;
  currency: Currency;
  disabled: boolean;
}) {
  const totalCoverageSharePercent =
    (Number(processingCoverageSharePercentValue) || 0) +
    (Number(consolidationCoverageSharePercent.value) || 0);
  const exceedsCoverageShareLimit = totalCoverageSharePercent > 100;

  return (
    <Flex dir="column" gap="xl" pt="lg">
      {exceedsCoverageShareLimit && (
        <Text fontSize="xs" textColor="error">
          {`Total coverage percentage (${totalCoverageSharePercent.toFixed(2)}%) cannot exceed 100%`}
        </Text>
      )}
      <Flex dir="column" gap="sm">
        <Flex align="center" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Paid for by coverage
          </Text>
          <Link to="../returns/return-protection">
            <LinkExternal02Icon
              className={`${styles.linkIcon} ${styles.linkIconSm}`}
            />
          </Link>
        </Flex>
        <RedoTextInput
          IconTrailing={() => <Text as="span">%</Text>}
          inputTypeHint="number"
          max={100}
          min={0}
          placeholder="0"
          setValue={consolidationCoverageSharePercent.setValue}
          state={
            disabled
              ? "disabled"
              : exceedsCoverageShareLimit
                ? "error"
                : "default"
          }
          step={0.01}
          value={consolidationCoverageSharePercent.value}
        />
        <CoverageSharePreview
          coveragePrice={coveragePrice}
          currency={currency}
          percentValue={consolidationCoverageSharePercent.value}
        />
        <Text fontSize="xs" textColor="secondary">
          If empty, consolidation shipping costs are passed through to the
          merchant.
        </Text>
      </Flex>
    </Flex>
  );
});

// ── Step content: Refulfillment centers ──────────────────────────────────────

// ── Step content: Order routing ─────────────────────────────────────────────

const OrderRoutingStep = memo(function OrderRoutingStep({
  teamId,
  inventoryPushMinimumQuantity,
  disabled,
}: {
  teamId: string;
  inventoryPushMinimumQuantity: InputSchema.Input<
    ReturnType<typeof input<string>>
  >;
  disabled: boolean;
}) {
  const team = useContext(TeamContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const openMerchantApp = useMerchantAppLogin();
  const shopifyAdminUrl = team?.storeUrl
    ? buildShopifyAdminUrl(team.storeUrl)
    : null;

  const { value: integrationsData } = useRpcData(
    rpcClient,
    "getManagedReturnsIntegrations",
    { input: { teamId } },
  );
  const integrations = useMemo(
    () => integrationsData?.integrations ?? [],
    [integrationsData],
  );

  const handleOpenShopify = useCallback(() => {
    if (shopifyAdminUrl) {
      window.open(shopifyAdminUrl, "_blank", "noopener,noreferrer");
    }
  }, [shopifyAdminUrl]);

  return (
    <Flex dir="column" gap="lg" pt="lg">
      <RedoTextInput
        description="Minimum source inventory required before refulfillment inventory is exposed to sales channels."
        inputMode="numeric"
        inputTypeHint="number"
        label="Inventory push buffer"
        min={0}
        setValue={inventoryPushMinimumQuantity.setValue}
        state={disabled ? "disabled" : "default"}
        step={1}
        value={inventoryPushMinimumQuantity.value}
      />
      {integrations.length > 0 && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Integrations
          </Text>
          <Text fontSize="xxs" textColor="secondary">
            Manage returns, orders, and inventory settings for each order
            provider
          </Text>
        </Flex>
      )}
      {integrations.map((integration) => (
        <Flex
          align="center"
          className={`${styles.roundedBorder} ${styles.linkRow}`}
          gap="lg"
          key={integration.provider}
          onClick={() =>
            openMerchantApp(
              teamId,
              `/settings/warehouse/integrations/${integration.provider}`,
            )
          }
          p="lg"
        >
          <Flex align="center" className={styles.providerIcon} justify="center">
            <Text fontSize="xxs" fontWeight="bold" textColor="secondary">
              {integration.name.slice(0, 2).toUpperCase()}
            </Text>
          </Flex>
          <Flex dir="column" gap="xxs" grow="1">
            <Text fontSize="xs" fontWeight="semibold">
              {integration.name}
            </Text>
            <Text fontSize="xxs" textColor="secondary">
              Redo Managed Returns refulfillment
            </Text>
          </Flex>
          {integration.enabled ? (
            <RedoBadge
              color="success"
              segmentLeading={{ type: "icon", Icon: CheckIcon }}
              size="sm"
              text="Enabled"
            />
          ) : (
            <RedoBadge color="gray" size="sm" text="Disabled" />
          )}
          <ChevronRightIcon className={styles.linkIcon} />
        </Flex>
      ))}
      {shopifyAdminUrl && (
        <Flex align="center" gap="xxs">
          <Text
            fontSize="xs"
            fontWeight="medium"
            onClick={handleOpenShopify}
            // design-allow: pre-existing inline style, unchanged
            style={{ cursor: "pointer" }}
            textColor="brand-primary"
          >
            Shopify routing
          </Text>
          <LinkExternal02Icon
            className={`${styles.linkIcon} ${styles.linkIconSm}`}
            onClick={handleOpenShopify}
            // design-allow: pre-existing inline style, unchanged
            style={{ cursor: "pointer" }}
          />
        </Flex>
      )}
    </Flex>
  );
});

// ── Step content: Refulfillment billing ──────────────────────────────────────

const RefulfillmentBillingStep = memo(function RefulfillmentBillingStep({
  merchantOwned,
  shippingMarkup,
}: {
  merchantOwned: InputSchema.Input<typeof reFulfillmentBillingConfigForm>;
  shippingMarkup: InputSchema.Input<typeof shippingMarkupConfigForm>;
}) {
  return (
    <Flex dir="column" gap="xl" pt="lg">
      <BillingPicker
        fixedLabel="Fixed fee per fulfillment"
        fixedSetValue={merchantOwned.inputs.fixedFeeAmount.setValue}
        fixedSuffix="USD"
        fixedValue={merchantOwned.inputs.fixedFeeAmount.value}
        mode={merchantOwned.inputs.billingMode.value}
        pctLabel="Percentage of order value"
        pctSetValue={(v: string) =>
          merchantOwned.inputs.revSharePercentage.setValue(Number(v) || 0)
        }
        pctSuffix="%"
        pctValue={String(merchantOwned.inputs.revSharePercentage.value)}
        setMode={merchantOwned.inputs.billingMode.setValue}
      />
      <Flex className={styles.borderTop} dir="column" gap="sm" pt="lg">
        <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
          Outbound shipping label markup
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Added to label cost when billing the merchant. Excludes UPS / USPS
          labels.
        </Text>
        <BillingPicker
          fixedLabel="Fixed fee per label"
          fixedSetValue={shippingMarkup.inputs.markupAmount.setValue}
          fixedSuffix="USD"
          fixedValue={shippingMarkup.inputs.markupAmount.value}
          mode={shippingMarkup.inputs.markupMode.value}
          pctLabel="Percentage of label cost"
          pctSetValue={(v: string) =>
            shippingMarkup.inputs.markupPercentage.setValue(Number(v) || 0)
          }
          pctSuffix="%"
          pctValue={String(shippingMarkup.inputs.markupPercentage.value)}
          setMode={shippingMarkup.inputs.markupMode.setValue}
        />
      </Flex>
    </Flex>
  );
});

// ── Team data helpers ───────────────────────────────────────────────────────

function billingConfigFromTeam(
  config: ReFulfillmentBillingConfig | undefined,
): InputSchema.Value<typeof reFulfillmentBillingConfigForm> {
  return {
    billingMode: config?.billingMode ?? "percentage",
    revSharePercentage: config?.revSharePercentage ?? 0,
    fixedFeeAmount: (config?.fixedFeeAmount ?? 0).toString(),
  };
}

function billingConfigToTeam(
  config: InputSchema.Value<typeof reFulfillmentBillingConfigForm>,
): ReFulfillmentBillingConfig {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    billingMode: config.billingMode as ReFulfillmentBillingMode,
    revSharePercentage: config.revSharePercentage,
    fixedFeeAmount: Number(config.fixedFeeAmount) || 0,
  };
}

// ── Error banner ────────────────────────────────────────────────────────────

const ErrorBanner = memo(function ErrorBanner({
  errors,
}: {
  errors: string[];
}) {
  if (errors.length === 0) {
    return null;
  }

  return (
    <div
      // design-allow: pre-existing fixed error toast positioning, unchanged
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 100,
        maxWidth: 400,
      }}
    >
      <RedoAlert
        color="error"
        description={errors.join(" | ")}
        title={`${errors.length} ${errors.length === 1 ? "error" : "errors"}`}
      />
    </div>
  );
});

// ── Automation exists modal ─────────────────────────────────────────────────

const AutomationExistsModal = memo(function AutomationExistsModal({
  automationId,
  teamId,
  entries,
  onClose,
}: {
  automationId: string;
  teamId: string;
  entries: { accountName: string; locationNames: string[] }[];
  onClose: () => void;
}) {
  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "View Automation",
        onClick: () => {
          window.open(automationUrl(teamId, automationId), "_blank");
          onClose();
        },
        size: "xs",
      }}
      secondaryButton={{ text: "Close", onClick: onClose, size: "xs" }}
      title="Automation Update Required"
    >
      <Flex dir="column" gap="lg">
        <Text>
          There is already an automation for this merchant. You will have to add
          these new locations manually:
        </Text>
        {entries.map((entry) => (
          <Flex dir="column" gap="xs" key={entry.accountName}>
            <Text fontWeight="semibold">{entry.accountName}</Text>
            <ul
              // design-allow: pre-existing list indent, unchanged
              style={{
                margin: "var(--redo-spacing-none)",
                paddingLeft: "var(--redo-spacing-2xl)",
              }}
            >
              {entry.locationNames.map((name) => (
                <li key={name}>
                  <Text>{name}</Text>
                </li>
              ))}
            </ul>
          </Flex>
        ))}
      </Flex>
    </RedoModal>
  );
});
